import React from 'react';
import {ModalBody, ModalFooter} from 'reactstrap';
import {APIClient} from "../../../utils/APIClient";
import {API_URL} from "../../../settings/local_settings";
import {Spinner} from "../../Spinner";
import {CHANNEL_ACCOUNT_SELECT_SPECIAL_ITEMS, ChannelAccountSelect} from "../../selects/ChannelAccountSelect";
import {ClientSelect} from "../../selects/ClientSelect";
import {CHANNEL_INFO_BY_CHANNEL, CHANNEL_TYPE_ANALYTICS, CHANNEL_TYPE_PAID_MEDIA, CHANNEL_TYPE_PROFITABILITY, CHANNEL_TYPE_SEO, ChannelsHelper, DV360_CHANNEL, ESCALAFY_CHANNEL, GOOGLE_CHANNEL, MERCADO_ADS_CHANNEL, SEARCH_CONSOLE_CHANNEL, SHEET_CHANNEL} from "../../channels/channels";
import {flagOf} from "../../channels/mercado_ads/mercadoAds";
import {ChannelLogo} from "../../channels/ChannelLogo";
import {AddAdAccountModal} from "../../channels/sheet/AddAdAccountModal";
import {ClientAdAccountFormBaseClass} from "./ClientAdAccountFormBase";
import { withTranslation } from 'react-i18next';
import {toast} from "react-toastify";
import {linkErrorMessage} from "../economia/EscalafyConnectWizard";
import {LinkAccountEmptyState} from "../control_central/LinkAccountEmptyState";
import {EMPTY_REASON, emptyAccountsReason, explainsEmptyAccounts} from "../control_central/linkAccountEmpty";
import {isPlanLimitError, publishAdAccountQuota, quotaFromLinkError, refetchAdAccountQuota} from "../../../utils/adAccountQuota";
import {planLimitText, recordQuotaEvent} from "../control_central/accountQuota";
import {
    fetchUnreachableLinked, findUnreachable, reconnectDataOfLinked, recordUnreachableShown, UnreachableLinkedNotice,
} from "../control_central/unreachableLinked";
import {isPaidReconnectChannel} from "../../agency/connections/reconnectAccountText";
import {ReconnectAdAccountModal} from "../../agency/connections/ReconnectAdAccountModal";

// La pantalla en el evento del rechazo por el cupo (`account_quota_rejected_link_form`).
const QUOTA_SURFACE = 'link_form';

// Polling reusado del patrón de la pantalla de Conexiones (AgencyConnectionsConfigurationContent).
const SYNC_POLL_INTERVAL_MS = 8000;
const SYNC_POLL_MAX_DURATION_MS = 5 * 60 * 1000;

// Canal del form -> key de credenciales en la respuesta de GET /agency/connections/. Reusamos el
// flag is_syncing que ese endpoint ya calcula por credencial (igual que la pantalla de Conexiones)
// para avisar que una conexión recién hecha todavía está trayendo sus cuentas.
const CHANNEL_TO_CONNECTIONS_KEY = {
    'GOOGLE': 'google_credentials',
    'FACEBOOK': 'facebook_credentials',
    'TIKTOK': 'tiktok_credentials',
    'LINKEDIN': 'linkedin_credentials',
    [DV360_CHANNEL]: 'dv360_credentials',
    [MERCADO_ADS_CHANNEL]: 'mercado_ads_credentials',
    'ANALYTICS': 'ga4_credentials',
    [SEARCH_CONSOLE_CHANNEL]: 'gsc_credentials',
    [ESCALAFY_CHANNEL]: 'escalafy_credentials',
};

// Canal del form -> key en el state donde se guardan sus cuentas (para refrescar al terminar el sync).
const CHANNEL_TO_ACCOUNTS_STATE_KEY = {
    'GOOGLE': 'allGoogleAccounts',
    'FACEBOOK': 'allFacebookAccounts',
    'TIKTOK': 'allTiktokAccounts',
    'LINKEDIN': 'allLinkedinAccounts',
    [DV360_CHANNEL]: 'allDV360Accounts',
    [MERCADO_ADS_CHANNEL]: 'allMercadoAdsAccounts',
    'ANALYTICS': 'allAnalyticsAccounts',
    [SEARCH_CONSOLE_CHANNEL]: 'allGscAccounts',
    [ESCALAFY_CHANNEL]: 'allEscalafyAccounts',
};

// Un vendedor de Mercado Libre tiene un anunciante por país: el combo suma bandera y moneda a cada uno.
function describeMercadoAdsAccount(account) {
    const currency = account && (account.currency_code || account.currency);
    const flag = flagOf(account);
    return [flag, currency].filter(Boolean).join(' ') || null;
}

export class AddClientAdAccountFormClass extends ClientAdAccountFormBaseClass {

    constructor(props) {
        super(props);

        const adAccountIsAlreadySelected = (this.props.channel !== undefined && this.props.adAccountId !== undefined);
        const selectedAccount = this.props.adAccountId !== undefined ? this.props.adAccountId : null;
        const channel = this.props.channel !== undefined ? this.props.channel : GOOGLE_CHANNEL;

        this.state = {
            ...this.state,
            creatingClientAdAccount: false,
            allGoogleAccounts: null,
            allFacebookAccounts: null,
            allTiktokAccounts: null,
            allLinkedinAccounts: null,
            allDV360Accounts: null,
            allMercadoAdsAccounts: null,
            allSheetAccounts: null,
            allAnalyticsAccounts: null,
            allGscAccounts: null,
            allEscalafyAccounts: null,
            agencyChannels: null,
            agencyAnalyticsChannels: null,
            agencySEOChannels: null,
            agencyProfitabilityChannels: null,
            channel: channel,
            adAccountIsAlreadySelected: adAccountIsAlreadySelected,
            selectedAccount: selectedAccount,
            selectedClient: null,
            agencyClients: null,
            hasClickedOnSubmit: false,
            dailyLimitEnabled: false,
            connectionsByChannel: null,
            connectionsFailed: false,
            // Las cuentas de ese cliente y canal que perdieron el acceso (M6, UD-21): las reconoce la
            // búsqueda. `key` = «cliente|canal» de lo que se pidió, para descartar una respuesta vieja.
            unreachableLinked: {key: null, rows: []},
            reconnectRow: null
        };
        this._unreachableSeen = new Set();

        // Estado del polling de sincronización (mismo patrón que la pantalla de Conexiones).
        this._syncPollingTimeout = null;
        this._syncPollingStartedAt = null;
        this._prevSyncingChannels = new Set();

        this.handleSelectedChannelChange = this.handleSelectedChannelChange.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleAccountChange = this.handleAccountChange.bind(this);
        this.handleClientChange = this.handleClientChange.bind(this);
        this.handleDailyLimitEnabledChange = this.handleDailyLimitEnabledChange.bind(this);
        this.afterSuccessfulSheetAdAccountSubmit = this.afterSuccessfulSheetAdAccountSubmit.bind(this);
        this.footerButton = this.footerButton.bind(this);
        this.refreshSelectedChannel = this.refreshSelectedChannel.bind(this);
        this.handleWindowFocus = this.handleWindowFocus.bind(this);
        this.unreachableNoResultsText = this.unreachableNoResultsText.bind(this);
        this.handleAccountSearch = this.handleAccountSearch.bind(this);
    }

    componentDidMount() {

        this.getActiveAgencyClients();

        if(!this.state.adAccountIsAlreadySelected) {
            this.getAllFacebookAccounts();
            this.getAllTiktokAccounts();
            this.getAllLinkedinAccounts();
            this.getAllDV360Accounts();
            this.getAllGoogleAccounts();
            this.getAllSheetAccounts();
            this.getAllAnalyticsAccounts();
            this.getAllGscAccounts();
            this.getAgencyChannels();
            this.getAgencyAnalyticsChannels();
            this.getAgencySEOChannels();
            this.getAgencyProfitabilityChannels();
            this.updateConnectionsSyncStatus();
            this.loadUnreachableLinked();
            window.addEventListener('focus', this.handleWindowFocus);
        }
    }

    componentWillUnmount() {
        this._stopSyncPolling();
        window.removeEventListener('focus', this.handleWindowFocus);
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.channel !== this.state.channel || prevState.selectedClient !== this.state.selectedClient
            || prevProps.clientId !== this.props.clientId) {
            this.loadUnreachableLinked();
        }
    }

    unreachableKey() {
        return `${this.getClientId() || ''}|${this.state.channel || ''}`;
    }

    // Acompañamiento de la búsqueda: si falla, la búsqueda dice lo de siempre.
    loadUnreachableLinked() {
        // Con la cuenta ya elegida no hay búsqueda.
        if (this.state.adAccountIsAlreadySelected) return;
        const clientId = this.getClientId();
        const channel = this.state.channel;
        const key = this.unreachableKey();
        if (!clientId || !isPaidReconnectChannel(channel)) {
            this.setState({unreachableLinked: {key, rows: []}});
            return;
        }
        fetchUnreachableLinked(clientId, channel).then((rows) => {
            if (this.unreachableKey() === key) this.setState({unreachableLinked: {key, rows}});
        });
    }

    unreachableRows() {
        const {unreachableLinked} = this.state;
        return unreachableLinked && unreachableLinked.key === this.unreachableKey() ? unreachableLinked.rows : [];
    }

    handleAccountSearch(text, count) {
        const match = count === 0 ? findUnreachable(this.unreachableRows(), text) : null;
        if (match) recordUnreachableShown(this._unreachableSeen, this.state.channel, match);
    }

    /**
     * El vacío del combo: la cuenta vinculada sin acceso con «Reconectarla» (M6), o qué no se encontró
     * y en qué plataforma (el texto del modal del Panel). Antes decía «No hay resultados».
     */
    unreachableNoResultsText(text) {
        const {t, i18n} = this.props;
        const channel = this.state.channel;
        const needle = (text || '').trim();
        const match = findUnreachable(this.unreachableRows(), needle);
        if (match) {
            return <UnreachableLinkedNotice t={t} language={i18n && i18n.language} row={match} channel={channel}
                                            onReconnect={(row) => this.setState({reconnectRow: row})}/>;
        }
        const info = CHANNEL_INFO_BY_CHANNEL[channel];
        return t('control_central.link_account_no_match', {
            query: needle,
            channel: info ? info.Spanish : channel,
            interpolation: {escapeValue: false},
        });
    }

    // API

    getAllFacebookAccounts() {
        this.getAccountsForChannel('FACEBOOK', (accounts) => {
            this.setState({
                allFacebookAccounts: accounts
            })
        });
    }

    getAllTiktokAccounts() {
        this.getAccountsForChannel('TIKTOK', (accounts) => {
            this.setState({
                allTiktokAccounts: accounts
            })
        });
    }

    getAllLinkedinAccounts() {
        this.getAccountsForChannel('LINKEDIN', (accounts) => {
            this.setState({
                allLinkedinAccounts: accounts
            })
        });
    }

    getAllDV360Accounts() {
        this.getAccountsForChannel(DV360_CHANNEL, (accounts) => {
            this.setState({
                allDV360Accounts: accounts
            })
        });
    }

    // Sólo se piden si el backend manda el canal (`get-channels/PAID_MEDIA/`): Mercado Ads se habilita por
    // agencia, y a las demás no se les agrega un pedido que no necesitan.
    getAllMercadoAdsAccounts() {
        this.getAccountsForChannel(MERCADO_ADS_CHANNEL, (accounts) => {
            this.setState({
                allMercadoAdsAccounts: accounts
            })
        });
    }

    getAllGoogleAccounts() {
        this.getAccountsForChannel('GOOGLE', (accounts) => {
            this.setState({
                allGoogleAccounts: accounts
            })
        });
    }

    getAgencyChannels(){
        new APIClient(this.getAgencyChannelsURL()).call('GET').then((response) => {
            if (response.status === 'ERROR') {
                alert(response.error_message);
                this.setState({
                    agencyChannels: []
                })
            } else if (response.status === 'OK') {
                this.setState({
                    agencyChannels: response.data
                })
                if (Array.isArray(response.data) && response.data.includes(MERCADO_ADS_CHANNEL)) {
                    this.getAllMercadoAdsAccounts();
                }
            }
        });
    }

    getAgencyAnalyticsChannels(){
        new APIClient(this.getAgencyAnalyticsChannelsURL()).call('GET').then((response) => {
            if (response.status === 'ERROR') {
                this.setState({
                    agencyAnalyticsChannels: []
                })
            } else if (response.status === 'OK') {
                this.setState({
                    agencyAnalyticsChannels: response.data
                })
            }
        });
    }

    getAllSheetAccounts() {
        this.getAccountsForChannel('SHEET', (accounts) => {
            this.setState({
                allSheetAccounts: accounts
            })
        });
    }

    getAllAnalyticsAccounts() {
        this.getAccountsForChannel('ANALYTICS', (accounts) => {
            this.setState({
                allAnalyticsAccounts: accounts
            })
        });
    }

    getAllGscAccounts() {
        this.getAccountsForChannel(SEARCH_CONSOLE_CHANNEL, (accounts) => {
            this.setState({
                allGscAccounts: accounts
            })
        });
    }

    getAgencySEOChannels() {
        new APIClient(this.getAgencySEOChannelsURL()).call('GET').then((response) => {
            if (response.status === 'ERROR') {
                this.setState({
                    agencySEOChannels: []
                })
            } else if (response.status === 'OK') {
                this.setState({
                    agencySEOChannels: response.data
                })
            }
        });
    }

    // «Rentabilidad del negocio» (Escalafy). Sin el canal —sin módulo de Economía, sin credencial o un
    // backend anterior a la familia— la lista queda vacía en silencio: no hay nada que avisar.
    getAgencyProfitabilityChannels() {
        new APIClient(this.getAgencyProfitabilityChannelsURL()).call('GET').then((response) => {
            const channels = response && response.status === 'OK' && Array.isArray(response.data) ? response.data : [];
            this.setState({agencyProfitabilityChannels: channels});
            if (channels.includes(ESCALAFY_CHANNEL))
                this.getAllEscalafyAccounts();
        }).catch(() => this.setState({agencyProfitabilityChannels: []}));
    }

    // Las organizaciones conectadas que todavía no están vinculadas a ningún cliente de la agencia.
    getAllEscalafyAccounts() {
        new APIClient(this.getAccountsForChannelURL(ESCALAFY_CHANNEL)).call('GET').then((response) => {
            const accounts = response && response.status === 'OK' && Array.isArray(response.data) ? response.data : [];
            this.setState({allEscalafyAccounts: accounts});
        }).catch(() => this.setState({allEscalafyAccounts: []}));
    }

    getActiveAgencyClients() {
        new APIClient(this.getActiveAgencyClientsURL()).call('GET').then((response) => {
            if (response.status === 'ERROR') {
                alert(response.error_message);
                this.setState({
                    agencyClients: []
                })
            } else if (response.status === 'OK') {
                this.setState({
                    agencyClients: response.data
                })
            }
        });
    }

    getAccountsForChannel(channel, callback) {
        new APIClient(this.getAccountsForChannelURL(channel)).call('GET').then((response) => {
            if (response.status === 'ERROR') {
                alert(response.error_message);
                callback([]);
            } else if (response.status === 'OK') {
                callback(response.data)
            }
        });
    }

    // --- Aviso "Sincronizando…" para conexiones recién hechas ---------------------------------
    // El listado de cuentas ahora se sirve de lo ya sincronizado (AgencyAdAccountStatus, la unión
    // de TODAS las conexiones del canal). Para una conexión recién creada, sus cuentas aparecen
    // recién cuando termina el sync de descubrimiento; mientras tanto avisamos y, al terminar,
    // refrescamos el dropdown. Reusamos el flag is_syncing de /agency/connections/.

    updateConnectionsSyncStatus() {
        new APIClient(this.getAgencyConnectionsURL()).call('GET').then((response) => {
            if (response.status !== 'OK' || !response.data) {
                // Sin las conexiones, el vacío de un canal no sabe decir por qué (`UNKNOWN`).
                this.setState({connectionsFailed: true});
                return;
            }

            const connectionsByChannel = response.data;
            const nowSyncing = this._computeSyncingChannels(connectionsByChannel);

            // Canales que terminaron de sincronizar desde el último poll -> refrescar sus cuentas.
            this._prevSyncingChannels.forEach((channel) => {
                if (!nowSyncing.has(channel))
                    this._refetchAccountsForChannel(channel);
            });
            this._prevSyncingChannels = nowSyncing;

            this.setState({connectionsByChannel, connectionsFailed: false}, () => this._scheduleSyncPollIfNeeded());
        });
    }

    _computeSyncingChannels(connectionsByChannel) {
        const syncing = new Set();
        Object.keys(CHANNEL_TO_CONNECTIONS_KEY).forEach((channel) => {
            const credentials = connectionsByChannel[CHANNEL_TO_CONNECTIONS_KEY[channel]] || [];
            if (credentials.some((credential) => credential && credential.is_syncing))
                syncing.add(channel);
        });
        return syncing;
    }

    isSelectedChannelSyncing() {
        if (!this.state.connectionsByChannel)
            return false;
        const key = CHANNEL_TO_CONNECTIONS_KEY[this.state.channel];
        if (!key)
            return false;
        const credentials = this.state.connectionsByChannel[key] || [];
        return credentials.some((credential) => credential && credential.is_syncing);
    }

    _anyChannelSyncing() {
        return this.state.connectionsByChannel
            ? this._computeSyncingChannels(this.state.connectionsByChannel).size > 0
            : false;
    }

    _scheduleSyncPollIfNeeded() {
        if (!this._anyChannelSyncing()) {
            this._stopSyncPolling();
            return;
        }
        if (this._syncPollingStartedAt === null)
            this._syncPollingStartedAt = Date.now();
        if (Date.now() - this._syncPollingStartedAt > SYNC_POLL_MAX_DURATION_MS) {
            this._stopSyncPolling();
            return;
        }
        if (this._syncPollingTimeout)
            clearTimeout(this._syncPollingTimeout);
        this._syncPollingTimeout = setTimeout(() => {
            this._syncPollingTimeout = null;
            this.updateConnectionsSyncStatus();
        }, SYNC_POLL_INTERVAL_MS);
    }

    _stopSyncPolling() {
        if (this._syncPollingTimeout) {
            clearTimeout(this._syncPollingTimeout);
            this._syncPollingTimeout = null;
        }
        this._syncPollingStartedAt = null;
    }

    _refetchAccountsForChannel(channel) {
        const stateKey = CHANNEL_TO_ACCOUNTS_STATE_KEY[channel];
        if (!stateKey)
            return;
        this.getAccountsForChannel(channel, (accounts) => this.setState({[stateKey]: accounts}));
    }

    // --- Canal sin cuentas para vincular (UD-9) -------------------------------------------------
    // El combo decía sólo «No hay resultados.» y la agencia no sabía que le faltaba conectar la
    // plataforma (wa#219). Ahora el vacío dice por qué, con las conexiones que ya trae el polling.
    // Mientras un acceso sincroniza queda el aviso «Sincronizando…» de arriba, con el combo.

    emptyReasonOfSelectedChannel() {
        const channel = this.state.channel;
        const accounts = this.allAccountsOfChannel(channel);
        if (!Array.isArray(accounts) || accounts.length > 0 || !explainsEmptyAccounts(channel))
            return null;
        if (this.state.connectionsByChannel === null)
            return this.state.connectionsFailed ? EMPTY_REASON.UNKNOWN : null;
        const reason = emptyAccountsReason(this.state.connectionsByChannel, channel);
        return reason === EMPTY_REASON.SYNCING ? null : reason;
    }

    refreshSelectedChannel() {
        this._refetchAccountsForChannel(this.state.channel);
        this.updateConnectionsSyncStatus();
    }

    // Quien fue a Conexiones (otra pestaña) a conectar la plataforma vuelve y encuentra sus cuentas.
    handleWindowFocus() {
        if (this.emptyReasonOfSelectedChannel() !== null)
            this.refreshSelectedChannel();
    }

    getClientId() {
        return this.props.clientId ? this.props.clientId : this.state.selectedClient
    }


    handleSubmit(event) {
        event.preventDefault();

        // El error lo muestra el componente. Se entera porque se le pasa el hasClickedOnSubmit
        if(this.state.selectedAccount === null || CHANNEL_ACCOUNT_SELECT_SPECIAL_ITEMS.includes(this.state.selectedAccount))
        {
            this.setState({hasClickedOnSubmit: true});
            return;
        }

        this.setState({
            creatingClientAdAccount: true,
        });

        new APIClient(this.submitUrl()).call('POST', this.getDataToPost(event)).then((data) => {
            this.setState({
                creatingClientAdAccount: false
            });

            if (data.status !== 'OK' && this.state.channel === ESCALAFY_CHANNEL) {
                // Por código, nunca el texto crudo: «ya vinculada» nombra sólo a un cliente de ESTA agencia.
                toast.error(linkErrorMessage(data, this.props.t));
            } else if (isPlanLimitError(data)) {
                // El tope del plan con sus números y el mismo texto que el resto de las pantallas (UD-15), no
                // el `alert()` con el texto crudo del backend. Una tienda también cuenta para el cupo.
                this.notifyPlanLimit(data);
            } else if (data.status !== 'OK') {
                alert(data.error_message);
            } else if (data.status === 'OK') {
                this.props.afterSuccessfulSubmit(
                    data.data.agency_client_ad_account_id,
                    this.state.channel,
                    data.data.ad_account_id
                )
            }
        });
    }

    // El rechazo trae el cupo y se difunde; si no lo trae, se relee: así un cartel del cupo en la pantalla de atrás se
    // entera. Con ese cartel (`quotaNoticeOnScreen`: Conexiones › Cuentas) el toast es el corto, con el verbo de la
    // pantalla, y no repite el aviso (QA de UD-15); sin él (Conexiones › Plataformas), el largo, con los números.
    notifyPlanLimit(response) {
        const quota = quotaFromLinkError(response);
        if (quota) publishAdAccountQuota(quota);
        else refetchAdAccountQuota();
        if (this.props.quotaNoticeOnScreen) {
            toast.error(this.props.t('account_quota.rejected_short_accounts'));
            return;
        }
        recordQuotaEvent('rejected', QUOTA_SURFACE);
        toast.error(planLimitText(this.props.t, quota));
    }

    getDataToPost(event) {

        const channel = this.state.adAccountIsAlreadySelected === true ? this.state.channel : event.target.campaignChannel.value;
        const ad_account_id = this.state.selectedAccount;
        // La familia de rentabilidad no tiene límite diario y el backend rechaza CUALQUIER tipo no nulo,
        // también el que el formulario base manda por defecto aunque la sección no se vea.
        const dailyLimit = ChannelsHelper.channelIsProfitability(channel)
            ? {'daily_limit_type': null, 'daily_limit_currency': null, 'daily_limit_value': null}
            : this._getDataToPost();

        return {
            'client_id': this.getClientId(),
            'account_id': ad_account_id.toString(),
            'channel': channel,
            ...dailyLimit
        };
    }

    handleDailyLimitEnabledChange(event) {
        this.setState({
            dailyLimitEnabled: event.target.checked
        });
    }

    currentChannel() {
        return this.state.channel;
    }

    handleSelectedChannelChange(event) {
        event.preventDefault();
        this.setState({
            channel: event.target.value,
            selectedAccount: null
        });
        this.alignDailyLimitTypeWithChannel(event.target.value);
    }

    handleAccountChange(accountId, accountName) {
        // Si selecciono "NUEVA CUENTA". Segun el canal seleccionado se muestra el modal de creacion de cuenta correspondiente
        if (this.state.channel === SHEET_CHANNEL && accountId === "CREATE_NEW_ACCOUNT") {
            this.addSheetAdAccountModal.toggle();
        }

        this.setState({
            selectedAccount: accountId
        })
    }

    handleClientChange(clientId, clientName) {
        this.setState({
            selectedClient: clientId
        })
    }

    footerButton() {
        const { t } = this.props;
        if (this.state.creatingClientAdAccount) {
            return <Spinner color="positive small"/>
        } else {
            // Sin cuentas no hay nada que agregar: el camino es el del vacío, no este botón.
            return <input type="submit" value={t('add_client_ad_account_form.add')} className="btn -submit"
                          disabled={this.emptyReasonOfSelectedChannel() !== null}/>
        }
    }

    addingAdAccountForAPredefinedClient() {
        return typeof this.props.clientId !== 'undefined' && this.props.clientId > 0;
    }

    renderClientInput() {
        if (this.addingAdAccountForAPredefinedClient()) {
            return (<input name="client" type="text" value={this.props.clientName} readOnly={true}
                           className="form-control pl-3"/>)
        } else {
            return (<ClientSelect selectedOption={this.state.selectedClient}
                                  allClients={this.state.agencyClients}
                                  onClientChange={this.handleClientChange}
                                  required={true}
            />)
        }
    }

    getAllAvailableChannels() {
        const paidMediaChannels = this.state.agencyChannels || [];
        const analyticsChannels = this.state.agencyAnalyticsChannels || [];
        const seoChannels = this.state.agencySEOChannels || [];
        const profitabilityChannels = this.state.agencyProfitabilityChannels || [];
        // Un canal que el bundle no conoce (un backend más nuevo que este Frontend) no se ofrece: no tiene
        // nombre, logo ni cuentas que listar.
        return [...paidMediaChannels, ...analyticsChannels, ...seoChannels, ...profitabilityChannels]
            .filter((channel) => CHANNEL_INFO_BY_CHANNEL[channel]);
    }

    handleChannelCardClick(channel) {
        this.setState({
            channel: channel,
            selectedAccount: null
        });
        this.alignDailyLimitTypeWithChannel(channel);
    }

    renderChannelsSelect() {
        const { t } = this.props;
        if(this.state.agencyChannels === null || this.state.agencyAnalyticsChannels === null || this.state.agencySEOChannels === null || this.state.agencyProfitabilityChannels === null)
            return <p>{t('common.loading')}</p>

        const allChannels = this.getAllAvailableChannels();

        return <div className="channel-selection-cards">
            {allChannels.map((channel) =>
                <div key={channel}
                     className={`channel-card${this.state.channel === channel ? ' -selected' : ''}`}
                     onClick={() => this.handleChannelCardClick(channel)}>
                    <span className="channel-card-icon">
                        <ChannelLogo channel={channel} width={18} height={18} hspace={0} vspace={0} marginLeft={0} padding={0} useOld={false}/>
                    </span>
                    <span className="channel-card-name">{CHANNEL_INFO_BY_CHANNEL[channel]?.Spanish || channel}</span>
                </div>
            )}
            <input type="hidden" name="campaignChannel" value={this.state.channel}/>
        </div>
    }

    renderSheetNewAddAccountModal() {
        return <AddAdAccountModal ref={instance => {
            this.addSheetAdAccountModal = instance;
        }}
                                  afterSuccessfulSubmit={this.afterSuccessfulSheetAdAccountSubmit}/>
    }

    afterSuccessfulSheetAdAccountSubmit(newSheetAdAccountData) {
        const allSheetAdAccounts = this.state.allSheetAccounts;
        const data = {
            'id': newSheetAdAccountData.account_id,
            'name': newSheetAdAccountData.name,
            'name_with_parents': newSheetAdAccountData.name,
            'currency_code': newSheetAdAccountData.currency,
            'parents_account_ids': []
        };

        allSheetAdAccounts.push(data);
        this.setState({
            allSheetAccounts: allSheetAdAccounts,
            selectedAccount: newSheetAdAccountData.account_id
        });
    }

    render() {
        const { t } = this.props;
        return [
            <form onSubmit={this.handleSubmit}>
                <ModalBody>
                    <div className="modal-section">
                        <div className="row g-3 mb-3">
                            <div className="col-6">
                                <label className="form-label">{t('add_client_ad_account_form.client')}</label>
                                {this.renderClientInput()}
                            </div>
                            <div className="col-6">
                                &nbsp;
                            </div>
                        </div>
                        {this.renderSelectAccountSection()}
                        {this.renderDailyLimitSection()}
                    </div>
                </ModalBody>
                <ModalFooter>
                    {this.footerButton()}
                </ModalFooter>
            </form>,
            this.renderSheetNewAddAccountModal(),
            this.state.reconnectRow && (
                <ReconnectAdAccountModal key="reconnect-linked" isOpen={true}
                                         onToggle={() => this.setState({reconnectRow: null})}
                                         adAccountData={reconnectDataOfLinked(this.state.reconnectRow)}
                                         onReconnected={() => this.setState({reconnectRow: null})}/>
            )
        ];
    }

    renderSelectAccountSection() {
        const { t } = this.props;
        if(this.state.adAccountIsAlreadySelected)
            return;

        return <div className="mb-3" id="account_selector_group">
            <label className="separator">{t('add_client_ad_account_form.account')}</label>
            <div className="mb-3">
                <label className="form-label">{t('link_account_modal.channel_label')}</label>
                {this.renderChannelsSelect()}
            </div>
            <div>
                <label className="form-label">{t('add_client_ad_account_form.account')}</label>
                {this.isSelectedChannelSyncing() &&
                    <div style={{display: 'flex', alignItems: 'center', gap: '0.5em', color: '#6B7280', marginBottom: '0.5em'}}>
                        <Spinner color="positive small"/>
                        <span>{t('add_client_ad_account_form.accounts_syncing')}</span>
                    </div>
                }
                {this.renderAccountChoice()}
            </div>
        </div>;
    }

    renderAccountChoice() {
        const emptyReason = this.emptyReasonOfSelectedChannel();
        if (emptyReason !== null) {
            return <LinkAccountEmptyState channel={this.state.channel}
                                          channelName={CHANNEL_INFO_BY_CHANNEL[this.state.channel]?.Spanish || this.state.channel}
                                          reason={emptyReason}
                                          onRefresh={this.refreshSelectedChannel}/>;
        }
        return <ChannelAccountSelect channel={this.state.channel}
                                     selectedAccount={this.state.selectedAccount}
                                     allAccounts={this.allAccountsOfChannel(this.state.channel)}
                                     onAccountChange={this.handleAccountChange}
                                     canCreateNewAccount={!!CHANNEL_INFO_BY_CHANNEL[this.state.channel]?.CreateAccount}
                                     showFormValidationTexts={this.state.hasClickedOnSubmit}
                                     describeAccount={this.state.channel === MERCADO_ADS_CHANNEL ? describeMercadoAdsAccount : undefined}
                                     noResultsText={this.unreachableNoResultsText}
                                     onSearchChange={this.handleAccountSearch}
        />;
    }

    renderDailyLimitSection() {
        if(!CHANNEL_INFO_BY_CHANNEL[this.state.channel]?.dailyLimitEnabled)
            return;

        return this.renderRadioButtonOptions();
    }

// aux

    allAccountsOfChannel(channel) {
        if (channel === 'GOOGLE') {
            return this.state.allGoogleAccounts
        } else if (channel === 'FACEBOOK') {
            return this.state.allFacebookAccounts
        } else if (channel === 'TIKTOK') {
            return this.state.allTiktokAccounts
        } else if (channel === 'LINKEDIN') {
            return this.state.allLinkedinAccounts
        } else if (channel === DV360_CHANNEL) {
            return this.state.allDV360Accounts
        } else if (channel === MERCADO_ADS_CHANNEL) {
            return this.state.allMercadoAdsAccounts
        } else if (channel === 'SHEET') {
            return this.state.allSheetAccounts
        } else if (channel === 'ANALYTICS') {
            return this.state.allAnalyticsAccounts
        } else if (channel === SEARCH_CONSOLE_CHANNEL) {
            return this.state.allGscAccounts
        } else if (channel === ESCALAFY_CHANNEL) {
            return this.state.allEscalafyAccounts
        }
    }

    // URLS

    getAgencyChannelsURL(channel) {
        return `${API_URL}get-channels/${CHANNEL_TYPE_PAID_MEDIA}/`
    }

    getAgencyAnalyticsChannelsURL() {
        return `${API_URL}get-channels/${CHANNEL_TYPE_ANALYTICS}/`
    }

    getAgencySEOChannelsURL() {
        return `${API_URL}get-channels/${CHANNEL_TYPE_SEO}/`
    }

    getAgencyProfitabilityChannelsURL() {
        return `${API_URL}get-channels/${CHANNEL_TYPE_PROFITABILITY}/`
    }

    getAccountsForChannelURL(channel) {
        return `${API_URL}get-accounts/${channel}`
    }

    getAgencyConnectionsURL() {
        return `${API_URL}agency/connections/`
    }

    getActiveAgencyClientsURL() {
        return `${API_URL}agency/clients/active`
    }

    submitUrl() {
        return API_URL + 'client/' + this.getClientId() + '/ad_account/add';
    }
}

export const AddClientAdAccountForm = withTranslation()(AddClientAdAccountFormClass);
export default AddClientAdAccountForm;
